import * as Sentry from '@sentry/browser'
import { SystemCollections } from 'components/dittoAppConnection'
import { useRPCClient } from 'components/hooks/rpc'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import { gunzip } from 'utils'
import { usePortalCallback } from 'utils'

import {
  DATA_REQUEST_PATHS,
  DeviceRequestType,
  dqlResultToDevice,
  LastUpdatedOption,
  lastUpdatedTimestamp,
  Params,
  reportError,
  Return,
} from './devices'

dayjs.extend(utc)

const SEARCH_CONDITION =
  "(device_name ILIKE :namePattern OR REGEXP_LIKE(_id, :idPattern, 'i'))"

const buildSearchArgs = (device: string, lastUpdated: LastUpdatedOption) => ({
  namePattern: `%${device.trim()}%`,
  idPattern: device.trim(),
  lastUpdated: lastUpdatedTimestamp(lastUpdated),
})

/**
 * A hook that provides device dashboard data and log requests operations,
 * using the RPC client
 */
export default function useDeviceRPC({ appId, cloudUrl }: Params): Return {
  const {
    rpcAccessToken,
    refreshToken,
    executeDqlQuery,
    executeLegacyUpdate,
    downloadAttachment,
  } = useRPCClient(appId, cloudUrl)

  useEffect(() => {
    Sentry.addBreadcrumb({
      level: 'debug',
      data: {
        appId,
      },
    })
  }, [appId])

  const getDeviceCount = usePortalCallback(
    async (lastUpdated: LastUpdatedOption) => {
      const response = await executeDqlQuery(
        `SELECT COUNT(*) FROM ${SystemCollections.SMALL_PEER_INFO_COLLECTION} WHERE last_updated_at > :lastUpdated`,
        {
          strictMode: false, // Force v5
          args: {
            lastUpdated: lastUpdatedTimestamp(lastUpdated),
          },
        },
      )

      // Returns the count as a number from the first item in the response
      // and the first value of the first item. This should allow for changes
      // to the way the count is retrieved in future, unless it is no longer
      // an object.
      const firstItem = response.items[0]
      if (!firstItem || typeof firstItem !== 'object') {
        return 0
      }
      return Object.values(firstItem)[0] as number
    },
    {
      operation: 'getDeviceCount',
      description: 'Fetch total device count from database',
      appId,
    },
  )

  const getDevicePage = usePortalCallback(
    async (page: number, pageSize: number, lastUpdated: LastUpdatedOption) => {
      if (page < 0 || pageSize < 1) {
        return []
      }

      const response = await executeDqlQuery(
        ` SELECT * FROM ${SystemCollections.SMALL_PEER_INFO_COLLECTION} WHERE last_updated_at > :lastUpdatedTime ORDER BY last_updated_at DESC LIMIT :limit OFFSET :offset`,
        {
          strictMode: false, // Force v5
          args: {
            limit: pageSize,
            offset: page * pageSize,
            lastUpdatedTime: lastUpdatedTimestamp(lastUpdated),
          },
        },
      )

      return response?.items?.map(dqlResultToDevice) || []
    },
    {
      operation: 'getDevicePage',
      description: 'Fetch paginated device list',
      appId,
    },
  )

  const getDevicePageAndCount = usePortalCallback(
    async (page: number, pageSize: number, lastUpdated: LastUpdatedOption) => {
      if (page < 0 || pageSize < 1) {
        return { devices: [], count: 0 }
      }

      const [devices, count] = await Promise.all([
        getDevicePage(page, pageSize, lastUpdated),
        getDeviceCount(lastUpdated),
      ])

      return { devices, count }
    },
    {
      operation: 'getDevicePageAndCount',
      description: 'Fetch devices and total device count',
      appId,
    },
  )

  const findDevice = useCallback(
    async (device: string, lastUpdated: LastUpdatedOption) => {
      try {
        if (!device?.trim()) {
          return []
        }

        const response = await executeDqlQuery(
          `SELECT * FROM ${SystemCollections.SMALL_PEER_INFO_COLLECTION} WHERE last_updated_at > :lastUpdated AND ${SEARCH_CONDITION} ORDER BY last_updated_at DESC`,
          {
            strictMode: false, // Force v5
            args: buildSearchArgs(device, lastUpdated),
          },
        )

        return response?.items ? response.items.map(dqlResultToDevice) : []
      } catch (error) {
        console.error('Failed to find device:', error)
        Sentry.captureException(error, {
          extra: { searchTerm: device, lastUpdated },
        })
        return []
      }
    },
    [executeDqlQuery],
  )

  const findDevicePaginated = useCallback(
    async (
      device: string,
      page: number,
      pageSize: number,
      lastUpdated: LastUpdatedOption,
    ) => {
      try {
        if (page < 0 || pageSize < 1 || !device?.trim()) {
          return []
        }

        const response = await executeDqlQuery(
          `SELECT * FROM ${SystemCollections.SMALL_PEER_INFO_COLLECTION} WHERE last_updated_at > :lastUpdated AND ${SEARCH_CONDITION} ORDER BY last_updated_at DESC LIMIT :limit OFFSET :offset`,
          {
            strictMode: false, // Force v5
            args: {
              ...buildSearchArgs(device, lastUpdated),
              limit: pageSize,
              offset: page * pageSize,
            },
          },
        )

        return response?.items ? response.items.map(dqlResultToDevice) : []
      } catch (error) {
        console.error('Failed to find devices (paginated):', error)
        Sentry.captureException(error, {
          extra: { searchTerm: device, page, pageSize, lastUpdated },
        })
        return []
      }
    },
    [executeDqlQuery],
  )

  const fetchSearchCount = useCallback(
    async (device: string, lastUpdated: LastUpdatedOption): Promise<number> => {
      try {
        if (!device || typeof device !== 'string') {
          return 0
        }

        if (!device.trim()) {
          return 0
        }

        const response = await executeDqlQuery(
          `SELECT COUNT(*) FROM ${SystemCollections.SMALL_PEER_INFO_COLLECTION} WHERE last_updated_at > :lastUpdated AND ${SEARCH_CONDITION}`,
          {
            strictMode: false, // Force v5
            args: buildSearchArgs(device, lastUpdated),
          },
        )

        if (!response?.items?.length) {
          return 0
        }

        const firstItem = response.items[0]
        if (!firstItem || typeof firstItem !== 'object') {
          return 0
        }

        const count = Object.values(firstItem)[0]

        if (typeof count === 'number' && !isNaN(count) && count >= 0) {
          return Math.floor(count)
        }

        return 0
      } catch (error) {
        console.error('Failed to fetch device search count:', error)
        Sentry.captureException(error, {
          extra: {
            searchTerm: device,
            lastUpdated,
          },
        })
        return 0
      }
    },
    [executeDqlQuery],
  )

  const fetchDevicesByPeerKeys = usePortalCallback(
    async (peerKeys: string[]) => {
      try {
        if (!peerKeys?.length) {
          return []
        }

        const validPeerKeys = peerKeys
          .filter((key) => key && typeof key === 'string')
          .slice(0, 100)
          .map((key) => `_id = '${key.trim()}'`)

        if (!validPeerKeys.length) {
          return []
        }

        const peerKeyQuery = validPeerKeys.join(' OR ')

        const response = await executeDqlQuery(
          `SELECT * FROM ${SystemCollections.SMALL_PEER_INFO_COLLECTION} WHERE ${peerKeyQuery}`,
          {
            strictMode: false, // Force v5
          },
        )

        return response?.items ? response.items.map(dqlResultToDevice) : []
      } catch (error) {
        console.error('Failed to fetch devices by peer keys:', error)
        Sentry.captureException(error, {
          extra: { peerKeyCount: peerKeys?.length },
        })
        return []
      }
    },
    {
      operation: 'fetchDevicesByPeerKeys',
      description: 'Batch fetch devices by peer keys',
      appId,
    },
  )

  const getDeviceByPeerKey = usePortalCallback(
    async (peerKey: string) => {
      try {
        if (!peerKey || typeof peerKey !== 'string') {
          return null
        }

        const response = await executeDqlQuery(
          `SELECT * FROM ${SystemCollections.SMALL_PEER_INFO_COLLECTION} WHERE _id = :peerKey`,
          {
            args: { peerKey: peerKey.trim() },
            strictMode: false, // Force v5
          },
        )

        return response?.items[0] ? dqlResultToDevice(response.items[0]) : null
      } catch (error) {
        console.error('Failed to get device by peer key:', error)
        Sentry.captureException(error, {
          extra: { peerKey },
        })
        return null
      }
    },
    {
      operation: 'getDeviceByPeerKey',
      description: 'Fetch single device by peer key',
      appId,
    },
  )

  /**
   * Helper function to fetch and unzip a single attachment
   */
  const fetchSingleAttachment = useCallback(
    async (attachmentId: string): Promise<Uint8Array[] | null> => {
      try {
        const bytes = await downloadAttachment(attachmentId)
        if (!bytes) {
          Sentry.captureMessage(
            `No bytes received for attachment ID: ${attachmentId}`,
            {
              level: 'info',
            },
          )
          return null
        }

        const uncompressedMembers = gunzip(bytes)
        const totalUncompressedSize = uncompressedMembers.reduce(
          (total, member) => total + member.length,
          0,
        )

        if (!totalUncompressedSize) {
          Sentry.captureMessage(
            `Received empty file for attachment ID: ${attachmentId}`,
            {
              level: 'warning',
            },
          )
        }

        return uncompressedMembers
      } catch (err) {
        reportError(err)
        return null
      }
    },
    [downloadAttachment],
  )

  const fetchLogs = useCallback(
    async (attachmentId: string) => {
      return fetchSingleAttachment(attachmentId)
    },
    [fetchSingleAttachment],
  )

  // Fetching a raw attachment is used for downloading compressed files such as tar.gz or zip files.
  const fetchRawAttachment = useCallback(
    async (
      attachmentId: string,
      attachmentType: string,
    ): Promise<Uint8Array[] | null> => {
      try {
        const bytes = await downloadAttachment(attachmentId)
        if (!bytes) {
          Sentry.captureMessage(
            `No bytes received for ${attachmentType} attachment ID: ${attachmentId}`,
            { level: 'info' },
          )
          return null
        }
        return [bytes]
      } catch (err) {
        reportError(err)
        return null
      }
    },
    [downloadAttachment],
  )

  const fetchBundle = useCallback(
    async (attachmentId: string) => fetchRawAttachment(attachmentId, 'bundle'),
    [fetchRawAttachment],
  )

  const fetchMetrics = useCallback(
    async (attachmentId: string) => fetchRawAttachment(attachmentId, 'metrics'),
    [fetchRawAttachment],
  )

  const fetchFlamegraph = useCallback(
    async (attachmentId: string) =>
      fetchRawAttachment(attachmentId, 'flamegraph'),
    [fetchRawAttachment],
  )

  const bulkRequestDeviceData = useCallback(
    async (peerKeys: string[], path: string) => {
      const now = dayjs.utc()
      await executeLegacyUpdate({
        query: `contains(${JSON.stringify(peerKeys)}, _id)`,
        collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
        updates: [
          {
            path,
            value: now.format(),
          },
        ],
      })
    },
    [executeLegacyUpdate],
  )

  const requestDeviceData = useCallback(
    async (peerKey: string, path: string) => {
      const now = dayjs.utc()
      await executeLegacyUpdate({
        query: `_id == "${peerKey}"`,
        collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
        updates: [
          {
            path,
            value: now.format(),
          },
        ],
      })
    },
    [executeLegacyUpdate],
  )

  const bulkRequestLogs = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestDeviceData(peerKeys, DATA_REQUEST_PATHS.logs.requestedAt)
    },
    [bulkRequestDeviceData],
  )

  const bulkRequestBundle = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestDeviceData(
        peerKeys,
        DATA_REQUEST_PATHS.bundle.requestedAt,
      )
    },
    [bulkRequestDeviceData],
  )

  const bulkRequestFlamegraph = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestDeviceData(
        peerKeys,
        DATA_REQUEST_PATHS.flamegraph.requestedAt,
      )
    },
    [bulkRequestDeviceData],
  )

  const bulkRequestMetrics = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestDeviceData(
        peerKeys,
        DATA_REQUEST_PATHS.metrics.requestedAt,
      )
    },
    [bulkRequestDeviceData],
  )

  const requestBundle = useCallback(
    async (peerKey: string) => {
      await requestDeviceData(peerKey, DATA_REQUEST_PATHS.bundle.requestedAt)
    },
    [requestDeviceData],
  )

  const requestLogs = useCallback(
    async (peerKey: string) => {
      await requestDeviceData(peerKey, DATA_REQUEST_PATHS.logs.requestedAt)
    },
    [requestDeviceData],
  )

  const requestFlamegraph = useCallback(
    async (peerKey: string) => {
      await requestDeviceData(
        peerKey,
        DATA_REQUEST_PATHS.flamegraph.requestedAt,
      )
    },
    [requestDeviceData],
  )

  const requestMetrics = useCallback(
    async (peerKey: string) => {
      await requestDeviceData(peerKey, DATA_REQUEST_PATHS.metrics.requestedAt)
    },
    [requestDeviceData],
  )

  const cancelDataRequest = useCallback(
    async (peerKey: string, requestType: DeviceRequestType) => {
      try {
        const path = DATA_REQUEST_PATHS[requestType].requestedAt
        await executeLegacyUpdate({
          query: `_id == "${peerKey}"`,
          collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
          updates: [
            {
              path,
              value: null,
            },
          ],
        })
        toast.success('Request cancelled successfully')
      } catch (error) {
        toast.error(`Failed to cancel ${requestType} request`)
        throw error
      }
    },
    [executeLegacyUpdate],
  )

  const bulkCancelDataRequests = useCallback(
    async (peerKeys: string[], requestType: DeviceRequestType) => {
      try {
        const path = DATA_REQUEST_PATHS[requestType].requestedAt
        await executeLegacyUpdate({
          query: `contains(${JSON.stringify(peerKeys)}, _id)`,
          collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
          updates: [
            {
              path,
              value: null,
            },
          ],
        })
        toast.success(
          `${requestType} requests cancelled for ${peerKeys.length} device(s)`,
        )
      } catch (error) {
        console.error('Failed to cancel data requests:', error)
        toast.error(`Failed to cancel ${requestType} requests`)
        throw error
      }
    },
    [executeLegacyUpdate],
  )

  return {
    accessToken: rpcAccessToken,
    refreshToken,
    bulkRequestLogs,
    bulkRequestBundle,
    bulkRequestMetrics,
    bulkRequestFlamegraph,
    cancelDataRequest,
    bulkCancelDataRequests,
    getDeviceCount,
    getDevicePage,
    getDevicePageAndCount,
    findDevice,
    findDevicePaginated,
    fetchSearchCount,
    fetchDevicesByPeerKeys,
    getDeviceByPeerKey,
    fetchBundle,
    fetchLogs,
    fetchFlamegraph,
    fetchMetrics,
    requestBundle,
    requestLogs,
    requestFlamegraph,
    requestMetrics,
  }
}

// Re-export types and utilities for convenience
export * from './devices'
